export type SupportedLanguage = 'en' | 'pt-BR' | 'es' | 'fr' | 'de' | 'it';

export const LANGUAGES: Array<{ code: SupportedLanguage; label: string }> = [
  { code: 'en', label: 'English' },
  { code: 'pt-BR', label: 'Português (Brasil)' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
  { code: 'de', label: 'Deutsch' },
  { code: 'it', label: 'Italiano' }
];

export interface UiCopy {
  brandTagline: string;
  homeHeading: string;
  homeSubheading: string;
  inputLabel: string;
  inputPlaceholder: string;
  trackButton: string;
  invalidCodeError: string;
  notFoundError: string;
  genericError: string;
  loadingText: string;
  originLabel: string;
  destinationLabel: string;
  currentLocationLabel: string;
  lastUpdatedLabel: string;
  historyHeading: string;
  upcomingHeading: string;
  footerNote: string;
  languageLabel: string;
  backHome: string;
  liveIndicator: string;
  orderReferenceLabel: string;
}

export const UI_COPY: Record<SupportedLanguage, UiCopy> = {
  en: {
    brandTagline: 'Track your package',
    homeHeading: 'Where’s my order?',
    homeSubheading: 'Enter your tracking code to see the latest updates.',
    inputLabel: 'Tracking code',
    inputPlaceholder: 'PKG-XX-XXXX-XXXX-XXXX',
    trackButton: 'Track package',
    invalidCodeError: 'That doesn’t look like a valid tracking code. Please check and try again.',
    notFoundError: 'We couldn’t find a shipment with that tracking code.',
    genericError: 'Something went wrong while loading your tracking information. Please try again in a moment.',
    loadingText: 'Loading tracking information…',
    originLabel: 'Shipped from',
    destinationLabel: 'Shipping to',
    currentLocationLabel: 'Current location',
    lastUpdatedLabel: 'Last updated',
    historyHeading: 'Tracking history',
    upcomingHeading: 'What happens next',
    footerNote: 'We’ll update your tracking page as your shipment moves. Thank you for your patience!',
    languageLabel: 'Language',
    backHome: 'Track another package',
    liveIndicator: 'Live',
    orderReferenceLabel: 'Order'
  },
  'pt-BR': {
    brandTagline: 'Rastreie seu pacote',
    homeHeading: 'Onde está meu pedido?',
    homeSubheading: 'Digite seu código de rastreio para ver as últimas atualizações.',
    inputLabel: 'Código de rastreio',
    inputPlaceholder: 'PKG-XX-XXXX-XXXX-XXXX',
    trackButton: 'Rastrear pacote',
    invalidCodeError: 'Esse não parece ser um código de rastreio válido. Verifique e tente novamente.',
    notFoundError: 'Não encontramos nenhuma remessa com esse código de rastreio.',
    genericError: 'Algo deu errado ao carregar as informações de rastreio. Tente novamente em instantes.',
    loadingText: 'Carregando informações de rastreio…',
    originLabel: 'Enviado de',
    destinationLabel: 'Enviando para',
    currentLocationLabel: 'Localização atual',
    lastUpdatedLabel: 'Última atualização',
    historyHeading: 'Histórico de rastreio',
    upcomingHeading: 'O que vem a seguir',
    footerNote: 'Atualizaremos sua página de rastreio conforme o envio avança. Obrigado pela paciência!',
    languageLabel: 'Idioma',
    backHome: 'Rastrear outro pacote',
    liveIndicator: 'Ao vivo',
    orderReferenceLabel: 'Pedido'
  },
  es: {
    brandTagline: 'Rastrea tu paquete',
    homeHeading: '¿Dónde está mi pedido?',
    homeSubheading: 'Ingresa tu código de seguimiento para ver las últimas novedades.',
    inputLabel: 'Código de seguimiento',
    inputPlaceholder: 'PKG-XX-XXXX-XXXX-XXXX',
    trackButton: 'Rastrear paquete',
    invalidCodeError: 'Ese no parece ser un código de seguimiento válido. Verifícalo e inténtalo de nuevo.',
    notFoundError: 'No encontramos ningún envío con ese código de seguimiento.',
    genericError: 'Ocurrió un error al cargar la información de seguimiento. Inténtalo de nuevo en un momento.',
    loadingText: 'Cargando información de seguimiento…',
    originLabel: 'Enviado desde',
    destinationLabel: 'Enviando a',
    currentLocationLabel: 'Ubicación actual',
    lastUpdatedLabel: 'Última actualización',
    historyHeading: 'Historial de seguimiento',
    upcomingHeading: 'Qué sigue',
    footerNote: 'Actualizaremos tu página de seguimiento a medida que avance tu envío. ¡Gracias por tu paciencia!',
    languageLabel: 'Idioma',
    backHome: 'Rastrear otro paquete',
    liveIndicator: 'En vivo',
    orderReferenceLabel: 'Pedido'
  },
  fr: {
    brandTagline: 'Suivez votre colis',
    homeHeading: 'Où est ma commande ?',
    homeSubheading: 'Saisissez votre numéro de suivi pour voir les dernières mises à jour.',
    inputLabel: 'Numéro de suivi',
    inputPlaceholder: 'PKG-XX-XXXX-XXXX-XXXX',
    trackButton: 'Suivre le colis',
    invalidCodeError: "Ce numéro de suivi ne semble pas valide. Vérifiez-le et réessayez.",
    notFoundError: 'Nous n’avons trouvé aucun envoi avec ce numéro de suivi.',
    genericError: 'Une erreur est survenue lors du chargement des informations de suivi. Réessayez dans un instant.',
    loadingText: 'Chargement des informations de suivi…',
    originLabel: 'Expédié depuis',
    destinationLabel: 'Expédié vers',
    currentLocationLabel: 'Position actuelle',
    lastUpdatedLabel: 'Dernière mise à jour',
    historyHeading: 'Historique de suivi',
    upcomingHeading: 'Prochaines étapes',
    footerNote: "Nous mettrons à jour votre page de suivi au fur et à mesure de l'expédition. Merci de votre patience !",
    languageLabel: 'Langue',
    backHome: 'Suivre un autre colis',
    liveIndicator: 'En direct',
    orderReferenceLabel: 'Commande'
  },
  de: {
    brandTagline: 'Sendung verfolgen',
    homeHeading: 'Wo ist meine Bestellung?',
    homeSubheading: 'Geben Sie Ihre Sendungsnummer ein, um die neuesten Updates zu sehen.',
    inputLabel: 'Sendungsnummer',
    inputPlaceholder: 'PKG-XX-XXXX-XXXX-XXXX',
    trackButton: 'Sendung verfolgen',
    invalidCodeError: 'Das sieht nicht nach einer gültigen Sendungsnummer aus. Bitte überprüfen und erneut versuchen.',
    notFoundError: 'Wir konnten keine Sendung mit dieser Nummer finden.',
    genericError: 'Beim Laden der Sendungsinformationen ist ein Fehler aufgetreten. Bitte versuchen Sie es gleich noch einmal.',
    loadingText: 'Sendungsinformationen werden geladen…',
    originLabel: 'Versendet von',
    destinationLabel: 'Versand nach',
    currentLocationLabel: 'Aktueller Standort',
    lastUpdatedLabel: 'Zuletzt aktualisiert',
    historyHeading: 'Sendungsverlauf',
    upcomingHeading: 'Wie es weitergeht',
    footerNote: 'Wir aktualisieren Ihre Sendungsseite, sobald sich der Status ändert. Vielen Dank für Ihre Geduld!',
    languageLabel: 'Sprache',
    backHome: 'Andere Sendung verfolgen',
    liveIndicator: 'Live',
    orderReferenceLabel: 'Bestellung'
  },
  it: {
    brandTagline: 'Traccia il tuo pacco',
    homeHeading: 'Dov’è il mio ordine?',
    homeSubheading: 'Inserisci il tuo codice di tracciamento per vedere gli ultimi aggiornamenti.',
    inputLabel: 'Codice di tracciamento',
    inputPlaceholder: 'PKG-XX-XXXX-XXXX-XXXX',
    trackButton: 'Traccia pacco',
    invalidCodeError: 'Questo non sembra un codice di tracciamento valido. Controllalo e riprova.',
    notFoundError: 'Non abbiamo trovato nessuna spedizione con questo codice di tracciamento.',
    genericError: 'Si è verificato un errore durante il caricamento delle informazioni di tracciamento. Riprova tra poco.',
    loadingText: 'Caricamento informazioni di tracciamento…',
    originLabel: 'Spedito da',
    destinationLabel: 'Spedito a',
    currentLocationLabel: 'Posizione attuale',
    lastUpdatedLabel: 'Ultimo aggiornamento',
    historyHeading: 'Cronologia di tracciamento',
    upcomingHeading: 'Prossimi passaggi',
    footerNote: 'Aggiorneremo la tua pagina di tracciamento man mano che la spedizione procede. Grazie per la pazienza!',
    languageLabel: 'Lingua',
    backHome: 'Traccia un altro pacco',
    liveIndicator: 'In diretta',
    orderReferenceLabel: 'Ordine'
  }
};

export function detectBrowserLanguage(): SupportedLanguage {
  const nav = typeof navigator !== 'undefined' ? navigator.language : 'en';
  if (nav.toLowerCase().startsWith('pt')) return 'pt-BR';
  const short = nav.slice(0, 2).toLowerCase();
  const match = LANGUAGES.find((l) => l.code === short);
  return match ? (short as SupportedLanguage) : 'en';
}
